import { Component, useContext } from 'react';
import { Button } from 'reactstrap';
import { Card, Row, Col } from 'react-bootstrap';
import Select from 'react-select';
import { ChartBar } from 'components/ChartBar';
import cx from 'classnames';
import { Loader } from 'components/Loader';
import { NotificationContext, NotificationContextState } from 'context/NotificationContext';
import * as apiCalls from 'logic/apiCalls';
import { triggerDownloadBlob } from 'logic/download';
import { RangeOption, rangeOptions } from 'logic/billingMetricLogic';
import { BillingMetricInfo, ChartData, ChartInfo, createChartSubText } from 'logic/billingMetricLogic';
import * as misc from 'utils/misc';

import css from './TabPerTenantUsage.module.scss';

import log from 'loglevel';


type PerTenantUsagePageState = {
    isLoading: boolean,
    isUpdating: boolean,
    showApiError: boolean,
    selectedGroupOption: RangeOption,
    selectedRange: string,
    resolution: string,
    showStacked: boolean
}

type PerTenantUsageInternalProps = {
    notificationContext: NotificationContextState
}

export class PerTenantUsageInternal extends Component<PerTenantUsageInternalProps, PerTenantUsagePageState> {
    state: PerTenantUsagePageState = {
        isLoading: true,
        isUpdating: false,
        showApiError: false,
        selectedGroupOption: rangeOptions[0],
        selectedRange: rangeOptions[0].value,
        resolution: rangeOptions[0].resolution,
        showStacked: rangeOptions[0].stacked

    }
    metricInfos: any[] = [];
    metricData: any[] = [];
    metricStartDate?: string | null = null;
    metricEndDate?: string | null = null;

    async componentDidMount() {
        await this.loadPageData();
    }
    async updateMetrics() {

        try {
            const metricsInfoPromise = apiCalls.getMetricInfos();
            const metricsPromise = apiCalls.getDistributorMetrics(null, this.state.selectedRange);

            const [metricInfos, distributorMetrics] = await Promise.all([metricsInfoPromise, metricsPromise]);

            this.metricInfos = metricInfos;
            const billingInfos: BillingMetricInfo[] = [];
            for (const metricInfo of metricInfos) {
                const data: ChartData[] = [];
                for (const tenant of distributorMetrics.tenants) {
                    if (Object.keys(tenant.data).length < 1) continue;
                    let val = 0.00;
                    for (const met of tenant.metrics) {
                        if (met.metric === metricInfo.metric) {
                            val = misc.round(met.metricTotalValue, 2);
                            break;
                        }
                    }
                    data.push({
                        date: tenant.tenantLabel,
                        value: val
                    });
                    this.metricStartDate = tenant.startDate;
                    this.metricEndDate = tenant.endDate;
                }
                const chartInfo: ChartInfo = {
                    data: data,
                    definitions: [{
                        displayName: metricInfo.unitOfMeasure,
                        valueName: "value"
                    }]
                };
                billingInfos.push({
                    displayName: metricInfo.displayName,
                    metric: chartInfo
                });
            }
            this.metricData = billingInfos;

            this.setState({
                isLoading: false,
                showApiError: false
            });
        }
        catch (error) {
            log.error(`Error during retrieval of the billing information:${error}`);

            this.setState({
                isLoading: false,
                showApiError: true
            });
        }
    }
    downloadExcel = async () => {
        try {
            const configurationScriptBlob = await apiCalls.getDistributorUsageReport(null, this.state.selectedRange);
            triggerDownloadBlob(configurationScriptBlob, 'distributor_usage_report.xlsx');
        }
        catch (error) {
            log.error(`Something went wrong: ${error}`);
            this.props.notificationContext.setNotification("Error", `Error during download of the usage report.`, 'danger');
        }
    }

    handleRangeChange = async (newValue: RangeOption | null) => {
        if (!newValue) return;

        try {
            this.setState({
                selectedGroupOption: newValue,
                selectedRange: newValue.value,
                resolution: newValue.resolution,
                showStacked: newValue.stacked,
                isUpdating: true,
                showApiError: false
            }, async () => await this.updateMetrics());


            this.setState({
                isUpdating: false,
                showApiError: false
            });
        }
        catch (error) {
            log.error(`Error during updating of the usage range:${error}`);

            this.setState({
                isUpdating: false,
                showApiError: true
            });
        }
    }
    createChartSubTextJsx = (chartData: any[], unitDisplayName: string): JSX.Element => {

        let [text, val] = createChartSubText(chartData, unitDisplayName);

        return <>{text} in this period:<b>{val}</b></>
    }

    loadPageData = async () => {
        try {
            await this.updateMetrics();

            this.setState({
                isLoading: false
            });
        }
        catch (error) {
            log.error(`Something went wrong: ${error}`);
        }
    }



    render() {
        return (
            <Loader isLoading={this.state.isLoading} showApiError={this.state.showApiError}>
                <>
                    <div>Select the usage period.</div>
                    <div className={css.rangeSelect}>
                        <Select
                            className="my-3"
                            inputValue=''
                            value={this.state.selectedGroupOption}
                            options={rangeOptions}
                            onChange={this.handleRangeChange}
                            placeholder='Select range...' />
                    </div>
                    <div className="my-3">Click the button below to download the usage information Excel sheet.</div>
                    <Button className='mt-3' color='primary' onClick={this.downloadExcel}>Download</Button>
                    <br />
                    {this.metricData.map(entry =>
                        <Row key={entry.displayName}>
                            <Col lg>
                                <Card className={cx("my-3", css.card)}>
                                    <Card.Header><h5>{entry.displayName}</h5>from {this.metricStartDate?.toString()} until {this.metricEndDate?.toString()}</Card.Header>
                                    <Card.Body>
                                        <ChartBar data={entry.metric.data} keyName="date" bars={entry.metric.definitions} isLoading={this.state.isUpdating} />
                                        {this.createChartSubTextJsx(entry.metric.data, entry.metric.definitions[0].displayName)}
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    )}
                </>
            </Loader>

        );
    }
}

// Inject the required contexts.
export const TabPerTenantUsageView = (props: any) => {
    const notificationContext = useContext(NotificationContext);

    return (
        <PerTenantUsageInternal {...props} notificationContext={notificationContext} />
    )
}